import './App.css';
import './Sidebar/Sidebar.css';
import Header from './Header/Header';
import App from './App';
import Modals from './Modals';
import SettingsPage from '@v2/pages/settings';

import React, { useEffect, useMemo } from 'react';
import {
    useMsalAuthentication,
    useIsAuthenticated,
    useMsal,
} from '@azure/msal-react';
import { InteractionStatus, InteractionType } from '@azure/msal-browser';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { actions } from '../context';
import hasPermission from './Shared/hasPermission';

const shrinkHeight = 1200;

const AppAuth = ({ msalClientScope }) => {

    const authenticationRequest = useMemo(() => ({
        scopes: [msalClientScope],
    }), [msalClientScope]);

    const dispatch = useDispatch();

    const isAuthenticated = useIsAuthenticated();

    const { login, error } = useMsalAuthentication(
        InteractionType.Redirect,
        authenticationRequest
    );

    const { inProgress } = useMsal();
    // when an error occurs it yells about app auth setting state while the msal provider is rendering
    const beginLogin = useMemo(() => {
        // If we can click the login button then something might be wrong with the redirect.
        // So we will dispatch the login action which will clear the cookies and session storage before trying to redirect again.
        // pass the login function from msal so it knows what to do.
        if (!error && !isAuthenticated && inProgress === InteractionStatus.None) {
            dispatch(
                actions.users.sessionLogin(
                    login,
                    InteractionType.Redirect,
                    authenticationRequest
                )
            );
        }

        return () => {
            if (!isAuthenticated && inProgress === InteractionStatus.None) {
                dispatch(
                    actions.users.sessionLogin(
                        login,
                        InteractionType.Redirect,
                        authenticationRequest
                    )
                );
            }
        }
        // eslint-disable-next-line
    }, [isAuthenticated, inProgress]);

    // Fetch data needed to run the app
    useEffect(() => {
        if (!error && isAuthenticated && inProgress === InteractionStatus.None) {
            dispatch(actions.auth.setAccountInfo());
            dispatch(actions.pims.fetchAllPims());
            dispatch(
                actions.toggle.toggleSidebarOpen(
                    window.innerWidth >= shrinkHeight
                )
            );
        }
        // eslint-disable-next-line
    }, [isAuthenticated, error, inProgress]);

    const { permissions, firstName, lastName, isAdmin, isPartnerUser, updateCount } = useSelector(
        (state) => ({
            permissions: state.auth.permissions,
            firstName: state.auth.firstName,
            lastName: state.auth.lastName,
            isAdmin: state.auth.isAdmin,
            isPartnerUser: state.auth.isPartnerUser,
            updateCount: state.meta.updateCount,
        }),
        shallowEqual
    );
    const canSeeAdmin = hasPermission(
        ['ViewAllPractices', 'Dashboard'],
        permissions
    );
    const { pathname } = useLocation();
    const isSettingsRoute = pathname.startsWith('/settings');
    const userName = [firstName, lastName].filter(Boolean).join(' ') || 'User';
    const userRole = isAdmin ? 'Admin' : isPartnerUser ? 'Partner' : 'User';

    const handleLogout = async () => {
        await dispatch(actions.users.sessionLogout());
    };

    const handleOpenUpdateLog = () => {
        dispatch(actions.toggle.toggleUpdateNotesModal(true));
    };

    return (
        <>
            <Modals />
            {!isSettingsRoute && (
                <Header isAuthenticated={isAuthenticated} login={beginLogin} />
            )}
            {isSettingsRoute && canSeeAdmin ? (
                <SettingsPage
                    userName={userName}
                    userRole={userRole}
                    updateCount={updateCount ?? 0}
                    onLogout={handleLogout}
                    onOpenUpdateLog={handleOpenUpdateLog}
                />
            ) : (
                <>
                    {/* these next two lines fix the header issues I was having. rj 04/21*/}
                    <br />
                    <span>&nbsp;</span>
                    <App beginLogin={beginLogin} error={error} />
                </>
            )}
        </>
    );
};

export default AppAuth;
